import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HotelIcon from '@mui/icons-material/HotelOutlined';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useDeleteBundleComponent } from '~/hooks/apis/bundle/useBundles';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOfferPackages } from '~/hooks/apis/offers/useGetOfferPackages';

import { formatDateWeekDayShortMonth } from '~/services/TimeService';

import { Bundle, HotelBundleComponent } from '~/types/bundles/bundle';

import HotelComponentDialog from './HotelComponentDialog';
import { hotelPackageOptions } from './hotelPackages';
import useBundleTourSpan from './useBundleTourSpan';

interface Props {
  bundle: Bundle;
}

function pluralise(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

interface HotelCardProps {
  bundle: Bundle;
  hotel: HotelBundleComponent;
  onRemove: () => void;
}

function HotelCard({ bundle, hotel, onRemove }: HotelCardProps) {
  const { data: offer, isLoading } = useGetOffer(hotel.offer_id);
  const { data: packages = [] } = useGetOfferPackages(hotel.offer_id);
  const packageName = useMemo(
    () => hotelPackageOptions(packages).find((option) => option.packageId === hotel.package_id)?.name,
    [packages, hotel.package_id],
  );

  const checkInDay = hotel.day_offset ?? 0;
  const firstDeparture = [...bundle.departure_dates].sort()[0];

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'secondary.main' }}>
            <HotelIcon />
          </Avatar>
        }
        title={
          <Typography variant="h6">{isLoading ? <Skeleton width={240} /> : (offer?.name ?? hotel.offer_id)}</Typography>
        }
        subheader={`Flash hotel · ${packageName ?? hotel.package_id}`}
        action={
          bundle.status === 'draft' && (
            <Tooltip title="Remove hotel">
              <IconButton aria-label="Remove hotel" onClick={onRemove}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          )
        }
      />
      <Divider />
      <CardContent>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Stay
            </Typography>
            <Typography>
              {pluralise(hotel.nights, 'night')} · day {checkInDay} to day {checkInDay + hotel.nights}
            </Typography>
          </Grid>
          {!!firstDeparture && (
            <Grid size={{ xs: 12, sm: 8 }}>
              <Typography variant="caption" color="text.secondary">
                For the {formatDateWeekDayShortMonth(firstDeparture)} departure
              </Typography>
              <Typography>
                Check in {formatDateWeekDayShortMonth(dayjs(firstDeparture).add(checkInDay, 'day').toDate())}, check out{' '}
                {formatDateWeekDayShortMonth(
                  dayjs(firstDeparture)
                    .add(checkInDay + hotel.nights, 'day')
                    .toDate(),
                )}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

export default function BundleHotelSection({ bundle }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hotelToRemove, setHotelToRemove] = useState<HotelBundleComponent | null>(null);

  const { tour } = useBundleTourSpan(bundle);
  const deleteComponent = useDeleteBundleComponent(bundle.id);

  const hotels = bundle.components.filter(
    (component): component is HotelBundleComponent => component.product_type === 'flashAccommodation',
  );
  const isDraft = bundle.status === 'draft';

  const handleRemove = async () => {
    try {
      await deleteComponent.mutateAsync(hotelToRemove.id);
      setHotelToRemove(null);
      enqueueSnackbar('Hotel removed', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  const addButton = isDraft && (
    <Tooltip title={tour ? '' : 'Add the tour first, so the hotel can be placed against its days'}>
      {/* Disabled buttons fire no events, so the tooltip needs a wrapper. */}
      <span>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsDialogOpen(true)} disabled={!tour}>
          Add hotel
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <Box>
      <PageSubheader title="Hotel">{hotels.length > 0 && <Box pb={1}>{addButton}</Box>}</PageSubheader>

      {hotels.length === 0 ? (
        <Paper variant="outlined" sx={{ borderStyle: 'dashed', p: 5, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
              <HotelIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">Add a flash hotel</Typography>
              <Typography color="text.secondary">
                Pair the tour with a flash hotel stay, placed on the days of the bundle it covers.
              </Typography>
            </Box>
            {addButton}
          </Stack>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {hotels.map((hotel) => (
            <HotelCard key={hotel.id} bundle={bundle} hotel={hotel} onRemove={() => setHotelToRemove(hotel)} />
          ))}
        </Stack>
      )}

      <HotelComponentDialog bundle={bundle} open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />

      <Dialog open={!!hotelToRemove} onClose={() => setHotelToRemove(null)}>
        <DialogTitle>Remove hotel?</DialogTitle>
        <DialogContent>
          <DialogContentText>Its prices and price adjustments are removed with it.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHotelToRemove(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRemove} disabled={deleteComponent.isPending}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
